import { createQuery } from '@tanstack/svelte-query'
import { fetchProjectGcode, getProject } from '../api/projects'
import queryClient from '../constants/query-client'

export const useProjectQuery = (projectId?: Project['id']) => createQuery(() => ({
  queryKey: ['project', projectId],
  queryFn: () => getProject(projectId),
  enabled: !!projectId,
  // `compressedThreeJsonUrl` is a freshly signed, expiring URL on every response,
  // so a refetch-on-focus would change it even when the scene is unchanged — in the
  // preview that re-triggers the scene load and flashes the loading screen. The
  // project only changes through client-initiated mutations (which invalidate this
  // query explicitly), so refetching on window focus buys us nothing here.
  refetchOnWindowFocus: false,
}), () => queryClient)

const GCODE_POLL_INTERVAL_MS = 2000

// How long we keep polling a still-pending generation before giving up. The
// preview surfaces this as an error (see ProjectPreview); the query uses it as a
// backstop so a silently dropped slicing job can't make us poll forever.
export const GCODE_POLL_TIMEOUT_MS = 60_000
const GCODE_MAX_POLLS = Math.ceil(GCODE_POLL_TIMEOUT_MS / GCODE_POLL_INTERVAL_MS)

// Shared source of truth for a project's gcode on a given printer. Both the
// preview (which parses it) and the download button (which reuses it) read from
// this single cache entry. The query polls every 2s until the generation resolves
// (ready or failed), both of which come back as data (see fetchProjectGcode).
export const useProjectGcodeQuery = (projectId?: Project['id'], printerId?: string) => createQuery(() => ({
  queryKey: ['project', projectId, 'gcode', printerId],
  queryFn: () => fetchProjectGcode(projectId!, printerId!),
  enabled: !!projectId && !!printerId,
  // Pending (202) and failed (422) are returned as data, so a *thrown* error here
  // is always transient (network blip, 5xx). Retry it a few times before letting it
  // surface — otherwise a single hiccup mid-poll would freeze the preview in error.
  retry: 3,
  retryDelay: 1000,
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchInterval: (query) => {
    const data = query.state.data
    // Generation resolved either way: stop polling.
    if (data?.status === 'ready' || data?.status === 'failed') {
      return false
    }
    // Transient failure that survived its retries: stop (the UI shows an error).
    if (query.state.status === 'error') {
      return false
    }
    // Backstop: never poll past the timeout window. `dataUpdateCount` counts each
    // successful (pending) poll and is reset by resetQueries, so the Retry button
    // gets a fresh window.
    if (query.state.dataUpdateCount >= GCODE_MAX_POLLS) {
      return false
    }
    return GCODE_POLL_INTERVAL_MS
  },
}), () => queryClient)
